'use client';

import * as React from 'react';
import { useAuth } from '@/firebase';
import { MediaItem } from '@/models/media-item';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { AircraftModelCombobox } from './aircraft-model-combobox';

export interface MediaUpdateFields {
    title: string;
    description: string;
    tags: string[];
    aircraftModelIds: string[];
    aircraftModelNames: string[];
    isPanorama: boolean;
}

interface EditMediaDialogProps {
    open: boolean;
    onClose: () => void;
    media: MediaItem;
    onUpdate: (mediaId: string, updates: MediaUpdateFields) => void;
}

export function EditMediaDialog({
    open,
    onClose,
    media,
    onUpdate
}: EditMediaDialogProps) {
    const auth = useAuth();
    const { toast } = useToast();

    const [title, setTitle] = React.useState(media.title);
    const [description, setDescription] = React.useState(media.description || '');
    const [tagInput, setTagInput] = React.useState('');
    const [tags, setTags] = React.useState<string[]>(media.tags || []);
    const [aircraftModelIds, setAircraftModelIds] = React.useState<string[]>(media.aircraftModelIds || []);
    const [aircraftModelNames, setAircraftModelNames] = React.useState<string[]>(media.aircraftModelNames || []);
    const [isPanorama, setIsPanorama] = React.useState(media.isPanorama || false);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setTitle(media.title);
            setDescription(media.description || '');
            setTags(media.tags || []);
            setTagInput('');
            setAircraftModelIds(media.aircraftModelIds || []);
            setAircraftModelNames(media.aircraftModelNames || []);
            setIsPanorama(media.isPanorama || false);
        }
    }, [open, media]);

    const handleAddTag = () => {
        if (!tagInput.trim()) return;
        const newTag = tagInput.trim().toLowerCase();
        if (!tags.includes(newTag)) {
            setTags([...tags, newTag]);
        }
        setTagInput('');
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toast({
                variant: 'destructive',
                title: 'Validation Error',
                description: 'Title is required'
            });
            return;
        }

        setSaving(true);
        const updates: MediaUpdateFields = {
            title: title.trim(),
            description: description.trim(),
            tags,
            aircraftModelIds,
            aircraftModelNames,
            isPanorama
        };

        try {
            const token = await auth.currentUser?.getIdToken();
            if (!token) throw new Error('Not authenticated');

            const res = await fetch('/api/gallery/update-media', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ mediaId: media.id, updates }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || err.error || 'Failed to save changes');
            }

            toast({
                title: 'Media Updated',
                description: 'Changes saved successfully'
            });

            onUpdate(media.id, updates);
            onClose();
        } catch (error) {
            console.error('Error updating media:', error);
            toast({
                variant: 'destructive',
                title: 'Error',
                description: error instanceof Error ? error.message : 'Failed to save changes'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit Media Details</DialogTitle>
                    <DialogDescription>
                        Update the title, description, and tags for this item.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Media title"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description (Optional)</Label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description..."
                            rows={3}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Tags</Label>
                        <div className="flex gap-2">
                            <Input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Add a tag..."
                            />
                            <Button type="button" onClick={handleAddTag} variant="outline">Add</Button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-2">
                            {tags.map(tag => (
                                <Badge key={tag} variant="secondary" className="px-2 py-1">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tag)}
                                        className="ml-2 hover:text-destructive"
                                    >
                                        ×
                                    </button>
                                </Badge>
                            ))}
                            {tags.length === 0 && (
                                <span className="text-sm text-muted-foreground italic">No tags</span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Aircraft Models</Label>
                        <AircraftModelCombobox
                            value={aircraftModelIds}
                            valueNames={aircraftModelNames}
                            onChange={(ids, names) => {
                                setAircraftModelIds(ids);
                                setAircraftModelNames(names);
                            }}
                            disabled={saving}
                        />
                    </div>

                    {media.type === 'photo' && (
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label htmlFor="panorama-toggle">360 Panorama</Label>
                                <p className="text-xs text-muted-foreground">
                                    Enable interactive 360 viewer for equirectangular images
                                </p>
                            </div>
                            <Switch
                                id="panorama-toggle"
                                checked={isPanorama}
                                onCheckedChange={setIsPanorama}
                                disabled={saving}
                            />
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving || !title.trim()}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
